/* eslint-disable react/require-default-props */
import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Buttons, FlexContainer, ModalContext, Modals, Text } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useGetUserCustomerData } from 'Containers/Datagathering/views/DgTypeForm/hooks';
import { useGetUserFlexData } from 'Containers/Dashboard/hooks';
import { useFetchGetUserClabe } from 'Containers/Account/components/AccountProfile/hooks';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import { stages } from 'Constants/stages';
import { ResultTypes } from 'Types/common';
import AmountToReceive from '../AmountToReceive';
import ToPayList from '../toPayList';
import ValidationInfo from '../ValidationInfo';
import * as Styled from './styles';
import { ResultInfoProps } from './types';
import {
  calculateFinalAmountForValidation,
  calculateBaseAmount,
  calculatePartialPrice,
  getContext,
} from './utils';

const ResultInfo = ({
  filingInfo,
  taxReturnResult,
  isUserWithPreFiling,
}: ResultInfoProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const { data } = useFetchGetUserClabe();
  const flexInfo = useGetUserFlexData();
  const userStage = useSelector(userStageSelector);
  const { showModal, hideModal } = useContext(ModalContext);
  const customerInfo = useGetUserCustomerData();
  const plan = useMemo(
    () =>
      customerInfo?.activePlan?.name
        ? plansNameMapping(customerInfo.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customerInfo]
  );

  const handleClick = (): void => {
    showModal({
      comp: (
        <Modals.Centered
          maxSize="XL"
          buttonsInline
          hasClose
          onClose={hideModal}
          primaryButton={{
            label: t('ResultInfo.modal'),
            onClick: () => hideModal(),
          }}
        >
          <AmountToReceive
            amountResolved={Math.abs(taxReturnResult?.taxReturnResult ?? 0)}
            partialAmount={filingInfo?.recent?.taxAgencyResolvedAmount ?? 0}
            flexFee={flexInfo.amountDue}
            isPartial={Boolean(filingInfo?.isPartial)}
          />
        </Modals.Centered>
      ),
    });
  };

  const lastPresentation = filingInfo?.recent;

  const isFlex = plan && plan === FRONT_PLANS.FLEX;

  const finalAmount = useMemo(() => {
    if (lastPresentation?.taxReturnType === ResultTypes.equal) {
      return 0;
    }
    if (userStage === stages.VALIDATION) {
      return calculateFinalAmountForValidation(lastPresentation?.amount, flexInfo?.amountDue);
    }
    const baseAmount = calculateBaseAmount(
      flexInfo.baseServiceAmount,
      lastPresentation?.amount,
      taxReturnResult?.taxReturnResult
    );

    return baseAmount;
  }, [lastPresentation?.amount, taxReturnResult?.taxReturnResult, isFlex, flexInfo?.amountDue]);

  return (
    <Styled.ResultInfoContainer direction="column" align="center" gap="dimension_08">
      <FlexContainer direction="column" align="center">
        <FlexContainer direction="column" gap="dimension_08" align="center">
          {filingInfo?.isPartial && (
            <Text type="paragraphs" color="grey" size="XS_M_S">
              {t('Amount.toReceive.partial', {
                price: YayoyakiFormatter.formatAmountMxn(Math.abs(lastPresentation?.amount ?? 0)),
              })}
            </Text>
          )}
          {userStage === stages.VALIDATION && (
            <ValidationInfo isFlex taxReturnResult={taxReturnResult} filingInfo={filingInfo} />
          )}
          <FlexContainer align="center">
            <Styled.AmountResolved
              type="paragraphs"
              size="S_M_S"
              $resultType={lastPresentation?.taxReturnType || taxReturnResult?.taxReturnType}
              $stage={userStage}
            >
              {t(
                `AmountResolved.Accepted.${lastPresentation?.taxReturnType || taxReturnResult?.taxReturnType}`,
                {
                  context: getContext(isFlex, userStage, lastPresentation, isUserWithPreFiling),
                }
              )}
            </Styled.AmountResolved>
            {filingInfo?.isPartial && (
              <Buttons.Icon icon="InfoXS" mode="warning_light" onClick={handleClick} size="xxs" />
            )}
          </FlexContainer>
        </FlexContainer>
        <Styled.AmountResolved
          type="paragraphs"
          size="L_M_S"
          $resultType={lastPresentation?.taxReturnType || taxReturnResult?.taxReturnType}
          $stage={userStage}
        >
          {filingInfo?.isPartial && filingInfo?.recent?.taxAgencyResolvedAmount
            ? YayoyakiFormatter.formatAmountMxn(calculatePartialPrice(flexInfo, filingInfo))
            : YayoyakiFormatter.formatAmountMxn(finalAmount)}
        </Styled.AmountResolved>
      </FlexContainer>
      {filingInfo?.recent?.taxReturnType === ResultTypes.toPay &&
        data?.installmentsListSelected &&
        data?.installmentsListSelected.length > 1 &&
        userStage === stages.FILED && <ToPayList data={data?.installmentsListSelected} />}
    </Styled.ResultInfoContainer>
  );
};

export default ResultInfo;
